import React, { useEffect, useMemo, useRef } from 'react';
import { Header } from 'new/elements/Form/Header';
import { Form } from 'new/elements/Form';
import { EnterCredentialPage } from 'new/components/flows/types/replication/CreateReplicationFlow/EnterCredentialPage';
import { SelectConnectionPage } from 'new/components/flows/types/replication/CreateReplicationFlow/SelectConnectionPage';
import { flowDataMap } from 'new/elements/flows/dataMap';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { FORM_TYPE_CREATE_REPLICATION } from 'js/constants/formTypes';
import { ResourceSetup } from 'new/components/flows/types/replication/CreateReplicationFlow/ResourceSetup';
import useReplication from 'new/components/flows/types/replication/useReplication';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import useNexsetsAdapter from 'new/state/nexsets/useNexsetsAdapter';
import flowTypes from 'new/components/flows/types/flowTypes';
import useFlowParams from 'new/components/flows/useFlowParams';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import { HeaderMetaConfigProvider } from 'new/components/flows/context/HeaderMetaConfigProvider';

interface CreateReplicationFlowProps {
  id?: number;
  flowType?: keyof typeof flowTypes;
  onDismiss?: (data: any) => void;
  isModal?: boolean;
  isEditMode?: boolean;
  resourceType?: 'source' | 'sink';
}

export const CreateReplicationFlow = (props: CreateReplicationFlowProps) => {
  const { isModal, onDismiss } = props;
  const { resourceType, id } = useFlowParams();

  const start = useRef(true);
  const {
    data: formData,
    connectionType,
    updateFormData,
    resetFormData,
    startForm,
    source,
    nexset,
    endForm,
  } = useReplication();

  const { pollNexset } = useFlowsAdapter();
  const { requestNexset } = useNexsetsAdapter();

  const request = useMemo(
    () =>
      pollNexset(async (foundNexset) => {
        const nexset = await requestNexset(foundNexset.id);
        updateFormData({ nexset });
      }),
    [],
  );

  useEffect(() => {
    // Only request flows when we are creating a sink and we don't have nexset info
    if (source && connectionType === 'SINK' && !nexset) {
      request(source?.origin_node_id);
    }
  }, [source, nexset, connectionType]);

  if (start.current) {
    startForm();
    updateFormData({
      connectionType: resourceType.toUpperCase(),
      flowType: 'replication',
    });
    start.current = false;
  }

  function resetFields() {
    resetFormData([
      'credentialId',
      'selectedCredentialInfo',
      'selectedConnection',
      'selectedBucket',
      'selectedTable',
      'table',
      'source',
      'sink',
      'nexset',
      'isNewTable',
      'sourceConfig',
      'sinkConfig',
    ]);
  }

  useEffect(() => {
    resetFields();
    return () => {
      resetFields();
      if (connectionType === 'SINK') {
        endForm();
      }
      request.cancel();
    };
  }, []);

  const onFormDismiss = () => {
    endForm();
    if (isModal) {
      onDismiss?.(formData);
      return true;
    }
    return false;
  };

  const flowMap = flowDataMap.replication;
  const titleResourceType = resourceType === 'source' ? 'Source' : 'Destination';

  return (
    <HeaderMetaConfigProvider>
      <Form
        formType={FORM_TYPE_CREATE_REPLICATION}
        isModal={isModal}
        isEditMode={!!id}
        onDismiss={onFormDismiss}
        HeaderComponent={
          <Header
            hideContextualMenu={isModal}
            title={
              <>
                {flowMap.shortTitle} <span style={{ color: 'var(--color-greyscale-200)' }}>/</span>{' '}
                {id ? 'Editing Data ' : 'New Data '}
                {titleResourceType}{' '}
              </>
            }
          />
        }
        description=""
        startPage={!!id ? 2 : undefined}
      >
        <SelectConnectionPage />
        <EnterCredentialPage />
        <ResourceSetup key={resourceType} />
      </Form>
    </HeaderMetaConfigProvider>
  );
};

const CreateReplicationFlowContent = (props: CreateReplicationFlowProps) => {
  return (
    <FlowParamsProvider {...props}>
      <CreateReplicationFlow {...props} />
    </FlowParamsProvider>
  );
};

export default CreateReplicationFlowContent;

CreateReplicationFlowContent.screenConfig = {
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/replication/:resourceType/:id/edit`,
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/replication/:resourceType/new`,
  ],
  screenName: 'ReplicationFlow',
  pageTitle: 'Replication Flow',
  hideSideBar: true,
  header: {
    title: 'Replication Flow',
    slugTitle: 'Replication Flow',
  },
};
